import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { http, formatApiError } from "@/api/http";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, ArrowLeft, Loader2 } from "lucide-react";
import AuthLayout from "@/components/AuthLayout";
import { FormAlert } from "@/components/auth/IconField";
import { ResendSentView } from "@/components/auth/ResendSentView";
import { isNative } from "@/lib/native";

export default function ResendSetup() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await http.post("/auth/resend-setup", { email: email.toLowerCase().trim(), language: i18n.language?.split("-")[0] || "pt" });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err, t("resendSetup.errorGeneric")));
    } finally {
      setLoading(false);
    }
  };

  if (sent) return <ResendSentView email={email} />;

  return (
    <AuthLayout
      icon={Mail}
      title={t("resendSetup.title")}
      subtitle={t("resendSetup.subtitle")}
      footer={
        <Link to="/login" data-testid="resend-login" className="text-primary font-medium hover:underline">
          <ArrowLeft className="w-3 h-3 inline mr-1" />{t("resendSetup.backToLogin")}
        </Link>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormAlert message={error} testId="resend-error" />
        <div className="space-y-2">
          <Label htmlFor="email">{t("resendSetup.emailLabel")}</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
            <Input id="email" type="email" autoComplete="email" autoFocus placeholder="seu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10 h-12" data-testid="resend-email-input" required />
          </div>
        </div>
        <Button type="submit" className="w-full h-12 font-medium" disabled={loading} data-testid="resend-submit-btn">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("resendSetup.submitting")}</> : t("resendSetup.submit")}
        </Button>
      </form>
      {!isNative() && <p className="text-xs text-muted-foreground text-center mt-6">
        {t("resendSetup.noEbookYet")}{" "}
        <a data-testid="resend-purchase-link" href="https://hotmart.com" target="_blank" rel="noreferrer" className="text-primary hover:underline">
          {t("resendSetup.learnMore")}
        </a>
      </p>}
    </AuthLayout>
  );
}
